import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useQueryClient } from 'react-query'

import { Button } from '../../../../../components/Button'
import { DialogModal } from '../../../../../components/DialogModal'
import { SearchInput } from '../../../../../components/SearchInput'
import { Medias } from '../../../../../enumns/medias'
import { useMediaQuery } from '../../../../../hooks/useMediaQuery'
import { NavigationSelect } from '../../components/NavigationSelect'
import { TableRowActions } from '../../components/TableRowActions'
import { TableWithManuallyPagination } from './components/TableWithManuallyPagination'
import { TableWithInfiniteScroll } from './components/TableWithInfiniteScroll'
import { AssetAnalystForm } from './components/AssetAnalystForm'
import { useAssetAnalystRemoveMutation } from '../../../../../services/hooks/assetAnalysts/mutations/useAssetAnalystRemoveMutation'
import { useAssetAnalystCreateMutation } from '../../../../../services/hooks/assetAnalysts/mutations/useAssetAnalystCreateMutation'
import { type AssetAnalystPersistence } from '../../../../../types/persistence/asset-analyst-persistence'
import { useAssetAnalystEditMutation } from '../../../../../services/hooks/assetAnalysts/mutations/useAssetAnalystEditMutation'
import { type AssetAnalystDomain } from '../../../../../types/domain/asset-analyst-domain'
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from '../../../../../constants/toast'
import { getErrorMessage } from '../../../../../utils/getErrorMessage'
import { type FiltersDomain } from '../../../../../types/domain/filters-domain'

export const AssetAnalyst = () => {
  const isMinScreenWidthLG = useMediaQuery(`(min-width: ${Medias.LG}px)`)
  const isMinScreenWidthMD = useMediaQuery(`(min-width: ${Medias.MD}px)`)

  const [assetAnalystToEdit, setAssetAnalystToEdit] =
    useState<AssetAnalystDomain>()

  const queryClient = useQueryClient()
  const assetAnalystRemotionMutation = useAssetAnalystRemoveMutation()
  const assetAnalystCreationMutation = useAssetAnalystCreateMutation()
  const assetAnalystEditionMutation = useAssetAnalystEditMutation()

  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [filters, setFilters] = useState<FiltersDomain>({
    page: 1,
    take: isMinScreenWidthMD ? 10 : 20,
    search: '',
    sort: 'createdAt',
    order: 'DESC',
  })

  const editAssetAnalyst = (id: number, data: AssetAnalystPersistence) => {
    assetAnalystEditionMutation.mutate(
      { id, data },
      {
        onSuccess () {
          toast.success(
            'Analista asset editado com sucesso',
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )

          setAssetAnalystToEdit(undefined)
          setIsFormModalOpen(false)
          queryClient.invalidateQueries(['asset-analysts', filters])
        },
        onError (error) {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
        },
      }
    )
  }

  const createAssetAnalyst = (data: AssetAnalystPersistence) => {
    assetAnalystCreationMutation.mutate(data, {
      onSuccess () {
        toast.success(
          'Analista asset cadastrado com sucesso',
          TOAST_SUCCESS_DEFAULT_OPTIONS
        )

        setIsFormModalOpen(false)
        queryClient.invalidateQueries(['asset-analysts', filters])
      },
      onError (error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  const handleSearchAssetAnalyst = (searchTerm: string) => {
    setFilters((prevState) => ({
      ...prevState,
      page: 1,
      search: searchTerm,
    }))
  }

  const handleRemoveAssetAnalyst = (id: number, successAction: () => void) => {
    assetAnalystRemotionMutation.mutate(id, {
      onSuccess: () => {
        toast.success(
          'Analista asset deletado com sucesso',
          TOAST_SUCCESS_DEFAULT_OPTIONS
        )

        queryClient.invalidateQueries(['asset-analysts', filters])
        successAction()
      },
      onError: (error) => {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  const handleSaveAssetAnalyst = async (data: AssetAnalystPersistence) => {
    if (assetAnalystToEdit) {
      return editAssetAnalyst(assetAnalystToEdit.id, data)
    }

    createAssetAnalyst(data)
  }

  const getAssetAnalystFromCache = (id: number) => {
    const assetAnalystFromCache: any = queryClient.getQueryData([
      'asset-analysts',
      filters,
    ])

    if (isMinScreenWidthMD) {
      return assetAnalystFromCache.data?.find(
        (assetAnalyst: AssetAnalystDomain) => assetAnalyst.id === id
      )
    }

    return assetAnalystFromCache?.pages
      ?.reduce((acc: any, item: { data: AssetAnalystDomain[] }) => {
        return [...acc, ...item.data]
      }, [])
      .find((assetAnalyst: AssetAnalystDomain) => assetAnalyst.id === id)
  }

  const handleCreateButtonClick = () => {
    if (assetAnalystToEdit) {
      setAssetAnalystToEdit(undefined)
    }

    setIsFormModalOpen(true)
  }

  const columns = [
    {
      field: 'id',
      headerName: 'id',
      isVisible: false,
    },
    {
      field: 'fullName',
      headerName: 'Nome',
      widthInPercentage: 50,
    },
    {
      field: 'asset',
      headerName: 'Asset',
      widthInPercentage: 40,
    },
    {
      field: 'actions',
      headerName: 'Ações',
      widthInPercentage: 10,
      renderCell ({ id }: { id: number }) {
        return (
          <TableRowActions
            tableContentType="AssetAnalyst"
            isRemovingRegister={assetAnalystRemotionMutation.isLoading}
            alertMessage="Você está prestes a excluir um Analista Asset. Deseja continuar?"
            onEdit={() => {
              const assetAnalystFromCache = getAssetAnalystFromCache(id)
              setAssetAnalystToEdit(assetAnalystFromCache)
              setIsFormModalOpen(true)
            }}
            onRemove={(successAction) =>
              handleRemoveAssetAnalyst(id, successAction)
            }
          />
        )
      },
    },
  ]

  useEffect(() => {
    if (isMinScreenWidthMD) {
      setFilters((prevState) => ({
        ...prevState,
        page: 1,
      }))
      return
    }

    if (!isMinScreenWidthMD) {
      setFilters((prevState) => ({
        ...prevState,
        take: 20,
        page: 1,
      }))
    }
  }, [isMinScreenWidthMD])

  return (
    <>
      {!isMinScreenWidthLG && <NavigationSelect defaultRoute="asset-analyst" />}

      <div className="md:flex md:justify-between md:items-center md:mb-6">
        <h1 className="mb-4 font-medium text-grey-900 md:text-lg md:mb-0">
          Analista Asset
        </h1>

        <div className="mb-4 md:flex md:gap-2">
          <SearchInput
            onSearch={handleSearchAssetAnalyst}
            placeholder="Buscar por nome"
            className="mb-6 md:w-96 md:mb-0"
          />

          <Button className="h-10 ml-auto" onClick={handleCreateButtonClick}>
            Cadastrar
          </Button>
        </div>
      </div>

      {isMinScreenWidthMD ? (
        <TableWithManuallyPagination
          columns={columns}
          filters={filters}
          setFilters={setFilters}
        />
      ) : (
        <TableWithInfiniteScroll columns={columns} filters={filters} />
      )}

      <DialogModal
        isOpen={isFormModalOpen}
        title={`${assetAnalystToEdit ? 'Editar' : 'Cadastrar'} analista asset`}
        onClose={() => setIsFormModalOpen(false)}
      >
        <AssetAnalystForm
          isSubmitting={assetAnalystCreationMutation.isLoading}
          assetAnalyst={assetAnalystToEdit}
          onSubmit={handleSaveAssetAnalyst}
        />
      </DialogModal>
    </>
  )
}
